import { IBoundary } from '../abstract/Boundary';
import Entity, { IEntity } from '../abstract/Entity';
import { IWall } from '../abstract/Wall';
import { IElement } from './ElementModel';
import { IFloor } from './FloorModel';
import { IModel2D } from './Model2DModel';
import { IProduct } from './ProductModel';

export interface ISpace extends IEntity {
  floor: IFloor | null; 
  products: Array<IProduct>;
  elements: Array<IElement>;
  models2D: Array<IModel2D>;
  boundaries: Array<IBoundary>;
  walls: Array<IWall>;
}

class SpaceModel extends Entity implements ISpace {
  floor: IFloor | null; 

  products: Array<IProduct>;

  elements: Array<IElement>;

  models2D: Array<IModel2D>;

  boundaries: Array<IBoundary>;

  walls: Array<IWall>;

  constructor(
    floor: IFloor | null = null, 
    products: Array<IProduct> = [],
    elements: Array<IElement> = [],
    models2D: Array<IModel2D> = [],
    boundaries: Array<IBoundary> = [],
    walls: Array<IWall> = [],
  ) {
    super();
    this.floor = floor;
    this.products = products;
    this.elements = elements;
    this.models2D = models2D;
    this.boundaries = boundaries;
    this.walls = walls;
  }

  // --- Refactored Floor Methods ---

  getFloor(): IFloor | null {
    return this.floor;
  }
  setFloor(floor: IFloor | null): void {
    this.floor = floor;
  }
  
  hasFloor(): boolean {
    return this.floor !== null;
  }

  removeFloor(): void {
    this.floor = null;
  }

  // --- Product Methods (Unchanged) ---

  getProducts(): Array<IProduct> {
    return this.products;
  }

  getProductById(uuid: string) {
    return this.products.find((product) => product.uuid === uuid);
  }

  setProducts(products: Array<IProduct>): void {
    this.products = products;
  }

  setProductById(product: IProduct, uuid: string): boolean {
    const relatedIndex = this.products.findIndex((product) => product.uuid === uuid);
    if (relatedIndex === -1) return false;

    this.products[relatedIndex] = product;
    return true;
  }

  addProduct(product: IProduct) {
    const relatedIndex = this.products.findIndex((p) => p.uuid === product.uuid);
    if (relatedIndex === -1) {
      this.products.push(product);
    }
  }

  deleteProduct(uuid: string): boolean {
    const relatedIndex = this.products.findIndex((product) => product.uuid === uuid);
    if (relatedIndex === -1) return false;

    this.products.splice(relatedIndex, 1);
    return true;
  }

  // --- Element Methods (Unchanged) ---

  getElements(): Array<IElement> {
    return this.elements;
  }

  getElementById(uuid: string) {
    return this.elements.find((element) => element.uuid === uuid);
  }

  setElements(elements: Array<IElement>): void {
    this.elements = elements;
  }

  setElementById(element: IElement, uuid: string): boolean {
    const relatedIndex = this.elements.findIndex((element) => element.uuid === uuid);
    if (relatedIndex === -1) return false;

    this.elements[relatedIndex] = element;
    return true;
  }

  addElement(element: IElement) {
    const relatedIndex = this.elements.findIndex((e) => e.uuid === element.uuid);
    if (relatedIndex === -1) {
      this.elements.push(element);
    }
  }

  deleteElementById(uuid: string): boolean {
    const relatedIndex = this.elements.findIndex((element) => element.uuid === uuid);
    if (relatedIndex === -1) return false;

    this.elements.splice(relatedIndex, 1);
    return true;
  }

  getModels2D(): Array<IModel2D> {
    return this.models2D;
  }

  getModel2DById(uuid: string) {
    return this.models2D.find((modle2D) => modle2D.uuid === uuid);
  }

  setModels2D(models2D: Array<IModel2D>): void {
    this.models2D = models2D;
  }

  setModel2DById(model2D: IModel2D, uuid: string): boolean {
    const relatedIndex = this.models2D.findIndex((model2D) => model2D.uuid === uuid);
    if (relatedIndex === -1) return false;

    this.models2D[relatedIndex] = model2D;
    return true;
  }

  addModel2D(model2D: IModel2D) {
    const relatedIndex = this.models2D.findIndex((m2d) => m2d.uuid === model2D.uuid);
    if (relatedIndex === -1) {
      this.models2D.push(model2D);
    }
  }

  deleteModel2D(uuid: string): boolean {
    const relatedIndex = this.models2D.findIndex((element) => element.uuid === uuid);
    if (relatedIndex === -1) return false;

    this.models2D.splice(relatedIndex, 1);
    return true;
  }

  // --- Boundary Methods (Unchanged) ---

  getBoundaries(): Array<IBoundary> {
    return this.boundaries;
  }

  getBoundariesById(uuid: string) {
    return this.boundaries.find((boundary) => boundary.uuid === uuid);
  }

  setBoundaries(boundaries: Array<IBoundary>): void {
    this.boundaries = boundaries;
  }

  setBoundaryById(boundary: IBoundary, uuid: string): boolean {
    const relatedIndex = this.boundaries.findIndex((boundary) => boundary.uuid === uuid);
    if (relatedIndex === -1) return false;

    this.boundaries[relatedIndex] = boundary;
    return true;
  }

  addBoundary(boundary: IBoundary): void {
    const relatedIndex = this.boundaries.findIndex((b) => b.uuid === boundary.uuid);
    if (relatedIndex === -1) {
      this.boundaries.push(boundary);
    }
  }

  getWalls(): Array<IWall> {
    return this.walls;
  }

  getWallById(uuid: string) {
    return this.walls.find((wall) => wall.uuid === uuid);
  }

  setWalls(walls: Array<IWall>): void {
    this.walls = walls;
  }

  setWallById(wall: IWall, uuid: string): boolean {
    const relatedIndex = this.walls.findIndex((wall) => wall.uuid === uuid);
    if (relatedIndex === -1) return false;

    this.walls[relatedIndex] = wall;
    return true;
  }

  deleteWallById(uuid: string): boolean {
    const relatedIndex = this.walls.findIndex((wall) => wall.uuid === uuid);
    if (relatedIndex === -1) return false;

    this.walls.splice(relatedIndex, 1);
    return true;
  }

  toJSON() {
    return {
      ...super.toJSON(),
      floor: this.floor ? (this.floor.toJSON?.() ?? this.floor) : null,
      products: this.products.map((p) => p.toJSON?.() ?? p),
      elements: this.elements.map((e) => e.toJSON?.() ?? e),
      models2D: this.models2D.map((m2d) => m2d.toJSON?.() ?? m2d),
      boundaries: this.boundaries.map((b) => b.toJSON?.() ?? b),
      walls: this.walls.map((w) => w.toJSON?.() ?? w),
    };
  }
}

export default SpaceModel;